import type { ActorKind } from './types'

/**
 * The stateless room message the backend sends the holder of a human lock when another member's
 * agent hits it (`lock-wanted`) or overrides it (`lock-forced`) — lock conflicts 6/8 (AB#301).
 * Mirrors backend/domain/lock-notice.ts#LockNotice; nothing about it is stored in the ydoc.
 */
export interface LockNotice {
  type: 'lock-wanted' | 'lock-forced'
  holder: { id: string; name: string }
  by: { id: string; name: string; kind: ActorKind }
  scope: {
    section?: string
    cells?: { rowIds: string[]; colIds: string[] }
    slide?: string
    elements?: string[]
  }
  prompt?: string
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null
const isWho = (v: unknown): v is { id: string; name: string } =>
  isRecord(v) && typeof v.id === 'string' && typeof v.name === 'string'

/**
 * A stateless payload as a lock notice, or null when it is anything else (another stateless
 * message, a malformed one). Read tolerantly: it is a network frame.
 */
export function parseLockNotice(payload: string): LockNotice | null {
  let v: unknown
  try {
    v = JSON.parse(payload)
  } catch {
    return null
  }
  if (!isRecord(v) || (v.type !== 'lock-wanted' && v.type !== 'lock-forced')) return null
  if (!isWho(v.holder) || !isWho(v.by) || !isRecord(v.scope)) return null
  const by = v.by as { id: string; name: string; kind?: unknown }
  return {
    type: v.type,
    holder: { id: v.holder.id, name: v.holder.name },
    by: { id: by.id, name: by.name, kind: by.kind === 'agent' ? 'agent' : 'human' },
    scope: v.scope as LockNotice['scope'],
    ...(typeof v.prompt === 'string' ? { prompt: v.prompt } : {}),
  }
}

/**
 * The identity of a notice on screen: a newer notice of the same kind, from the same member, over
 * the same thing replaces the older one instead of stacking under it.
 */
export function lockNoticeKey(n: LockNotice): string {
  return JSON.stringify([n.type, n.by.id, n.scope.section ?? '', n.scope.slide ?? '', n.scope.cells ?? null, n.scope.elements ?? null])
}
